import type { BranchDetailsItem } from '@/api/models';
import { formatGel } from '@/utils/money';

/**
 * Presentational config + row shape rendered by the Branches table.
 *
 * The row shape is the bos-api's `BranchDetailsItem` verbatim — the table is
 * wired to `useBranchDetailsQuery` and surfaces every field it returns.
 */

export type Branch = BranchDetailsItem;

export type BranchRowAction =
    'edit' | 'import' | 'archive' | 'unarchive' | 'delete';

/** Icon shown in the thumbnail cell when a branch has no image. */
export const NO_IMAGE_ICON = 'carbon:no-image';

/** Stable column ids — shared between the View columns and sort descriptor. */
export const BRANCH_COLUMN = {
    image: 'image',
    name: 'name',
    address: 'address',
    area: 'area',
    isMain: 'isMain',
    skuCount: 'skuCount',
    totalUnits: 'totalUnits',
    inventoryValue: 'inventoryValue',
    apiToken: 'apiToken',
    actions: 'actions',
} as const;

export const BRANCH_ROW_ACTIONS: {
    id: BranchRowAction;
    icon: string;
    labelKey: string;
    variant?: 'danger';
    /**
     * Whether this action belongs on an archived row (`true`), a live one
     * (`false`), or either (`undefined`).
     */
    onlyWhenArchived?: boolean;
    /**
     * Offered only where the session may import into the row's branch — see
     * `canImportProducts` in `../branchImportAccess.ts`.
     */
    requiresImportAccess?: boolean;
}[] = [
    {
        id: 'edit',
        icon: 'gravity-ui:pencil',
        labelKey: 'branches.components.branchTable.actions.edit',
    },
    {
        id: 'import',
        icon: 'gravity-ui:file-arrow-up',
        labelKey: 'branches.components.branchTable.actions.import',
        onlyWhenArchived: false,
        requiresImportAccess: true,
    },
    {
        id: 'archive',
        icon: 'gravity-ui:box',
        labelKey: 'branches.components.branchTable.actions.archive',
        onlyWhenArchived: false,
    },
    {
        id: 'unarchive',
        icon: 'gravity-ui:arrow-up-from-square',
        labelKey: 'branches.components.branchTable.actions.unarchive',
        onlyWhenArchived: true,
    },
    {
        id: 'delete',
        icon: 'gravity-ui:trash-bin',
        labelKey: 'branches.components.branchTable.actions.delete',
        variant: 'danger',
    },
];

/**
 * The row actions that apply to a branch in the given state. A branch is
 * archived or it is not, so offering both would always offer one no-op.
 * `canImport` is the session's verdict for this row (`canImportProducts`);
 * without it the row offers no Import products.
 */
export const branchRowActionsFor = (
    archived: boolean,
    canImport: boolean,
): typeof BRANCH_ROW_ACTIONS =>
    BRANCH_ROW_ACTIONS.filter(
        (action) =>
            (action.onlyWhenArchived === undefined ||
                action.onlyWhenArchived === archived) &&
            (!action.requiresImportAccess || canImport),
    );

/**
 * Display currency for branch inventory value. Hardcoded to GEL today — the
 * bos-api is single-currency (see Common/Currencies.cs). Lift to a per-tenant
 * value when multi-currency lands.
 */
/** Unit label for the branch `area` (square metres). */
export const AREA_UNIT = 'm²';

const AREA_FORMATTER = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

/**
 * Render the branch inventory value for the table cell. The value is a plain
 * inventory counter (server-stubbed to 0 today), not a balance signal, so there
 * is no +/- or colour treatment — but it is money, so it goes through
 * `formatGel` like every other amount in the app (BOS-162). It used to render
 * `319.80 GEL` beside accounting's `₾ 319.80`.
 */
export const formatInventoryValue = (valueTetri: number): string =>
    formatGel(valueTetri);

/**
 * Render the branch area. `null` ("unknown") renders as an em dash; a numeric
 * value is formatted with the square-metre unit.
 */
export const formatArea = (
    area: number | null,
    unitLabel: string = AREA_UNIT,
): string => {
    if (area === null) {
        return '—';
    }
    return `${AREA_FORMATTER.format(area)} ${unitLabel}`;
};

/** Default rows shown per page in the branches table (client-side pagination). */
export const BRANCHES_PAGE_SIZE = 10;

/** Selectable page sizes for the branches table's rows-per-page control. */
export const BRANCHES_PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

/**
 * Compute the page numbers to render in a pagination control, collapsing long
 * ranges with `'ellipsis'` markers (first/last always shown, plus a window
 * around the current page). Pure — unit-tested directly.
 */
export const getPaginationWindow = (
    totalPages: number,
    current: number,
): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i += 1) {
            pages.push(i);
        }
        return pages;
    }
    pages.push(1);
    if (current > 3) {
        pages.push('ellipsis');
    }
    const start = Math.max(2, current - 1);
    const end = Math.min(totalPages - 1, current + 1);
    for (let i = start; i <= end; i += 1) {
        pages.push(i);
    }
    if (current < totalPages - 2) {
        pages.push('ellipsis');
    }
    pages.push(totalPages);
    return pages;
};
